// i18n
import { translateInComponent } from 'infras/i18n';
import translations from '../i18n.json';

import { useState, SyntheticEvent, useMemo, useContext } from 'react';
import { useRouter } from 'next/router';

import { destroy } from 'infras/rest_client';
import { ViewerItem } from 'presentations/api/schema/user';
import {
  apiPath,
  hostTaxRateEditPath,
  hostTaxRateNewPath,
} from 'presentations/routes/path';
import { SubscriptionStateItem } from 'presentations/api/schema/subscription';
import {
  sortToViewerTopListItems,
  TeamUserListItem,
} from 'presentations/api/schema/team_user';
import { useRequest } from 'presentations/views/hooks/request';
import { TaxRateOptionIndex } from 'presentations/views/components/organisms/TaxRateList';
import {
  DashboardPortalContext,
  setErrorMessage,
} from 'presentations/reducers/DashboardPortal';
import { TaxRateListItem } from 'presentations/api/schema/tax_rate';

interface Props {
  viewer: ViewerItem;
}
const perPage = 20;

export function useTaxRateListPage(props: Props) {
  const t = translateInComponent(translations);

  const { dispatch } = useContext(DashboardPortalContext);

  const [isConfirmLoading, setIsConfirmLoading] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [deleteTaxRate, setDeleteTaxRate] = useState<TaxRateListItem | null>(
    null,
  );
  const [anchor, setAnchor] = useState<any>(null);

  const router = useRouter();
  const currentUserId = router.query.user ? String(router.query.user) : null;

  const taxRatesRequest = useRequest(
    apiPath(`/v1/teams/${props.viewer.team!.uid}/tax_rates`, {
      page: currentPage,
      perPage,
    }),
  );
  const taxRates: TaxRateListItem[] = taxRatesRequest.data || [];

  const onChangePage = async (e: React.ChangeEvent<unknown>, value: number) => {
    setCurrentPage(value);
  };

  const onDeleteTaxRate = async () => {
    if (!deleteTaxRate) return;
    setIsConfirmLoading(true);
    const result = await destroy(
      apiPath(
        `/v1/teams/${props.viewer.team!.uid}/tax_rates/${
          deleteTaxRate.uid
        }/delete`,
      ),
    );
    if (!result.isSuccess) {
      setDeleteTaxRate(null);
      setIsConfirmLoading(false);
      dispatch(setErrorMessage(t('Failed Delete Tax Rate')));
      return;
    }

    const newTaxRates = taxRates.filter((e) => e.id !== deleteTaxRate.id);
    taxRatesRequest.mutate({ ...taxRatesRequest, data: newTaxRates }, false);
    setDeleteTaxRate(null);
    setIsConfirmLoading(false);
  };

  const onClickNewTaxRate = async (e: SyntheticEvent<HTMLButtonElement>) => {
    router.push(hostTaxRateNewPath());
  };

  const onCloseDialog = () => {
    setDeleteTaxRate(null);
  };

  const onSelectMenu = async (
    taxRate: TaxRateListItem,
    selectedIndex: number,
  ) => {
    switch (selectedIndex) {
      case TaxRateOptionIndex.edit: {
        router.push(
          hostTaxRateEditPath({
            taxRateId: taxRate.uid,
          }),
        );
        break;
      }
      case TaxRateOptionIndex.delete: {
        setDeleteTaxRate(taxRate);
        break;
      }
      default:
        break;
    }
  };

  return {
    state: {
      taxRates,
      isLoading: taxRatesRequest.isLoading,
      currentPage,
      numberOfPages: taxRatesRequest.numberOfPages,
      isConfirmLoading,
      deleteTaxRate,
      anchor,
    },
    actions: {
      onChangePage,
      onSelectMenu,
      onDeleteTaxRate,
      onCloseDialog,
      onClickNewTaxRate,
    },
  };
}
